import {
  Controller,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";

import {
  DatePicker,
  ErrorMessage,
  InputText,
  Label,
  Select,
  type TErrorMessageKey,
  createValidation,
  getErrorMessage,
} from "@up/form";
import { CountryOptions } from "@up/ui";

import type { CSSProperties, PropsWithChildren } from "react";
import type { FieldErrors, UseFormRegister } from "react-hook-form";

type SelectProps = {
  control: any;
  formErrors: FieldErrors;
  formValues: Record<string, any>;
  register: UseFormRegister<any>;
};

type ElementWrapperProps = PropsWithChildren<{
  [x: PropertyKey]: any;
  className?: string;
  hasErrors?: boolean;
  hidden?: boolean;
}>;

type FormElementProps = {
  formErrors: FieldErrors;
  formValues: Record<string, any>;
  register: UseFormRegister<any>;
};

function ElementWrapper({
  children,
  className = "",
  hasErrors = false,
  hidden = false,
  ...rest
}: ElementWrapperProps) {
  if (hidden) {
    return null;
  }

  return (
    <div
      className={`kup-element ${className} ${
        hasErrors ? "kup-element-has-errors" : ""
      }`}
      {...rest}
    >
      {children}
    </div>
  );
}

export const Email = function ({
  formErrors,
  formValues,
  register,
}: FormElementProps) {
  const KEY = "email";
  const VALIDATION = { email: true, presence: true };
  const ERRORS = formErrors[KEY];

  return (
    <ElementWrapper hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} text="Email Address" />
      <InputText
        registerProps={register(KEY, createValidation(VALIDATION, formValues))}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

export const OTP = function ({
  formErrors,
  formValues,
  register,
}: FormElementProps) {
  const KEY = "otp";
  const VALIDATION = { presence: true };
  const ERRORS = formErrors[KEY];

  return (
    <ElementWrapper hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} required text="One-Time Password" />
      <InputText
        autoComplete="off"
        registerProps={register(KEY, createValidation(VALIDATION, formValues))}
        style={
          {
            "--kup-element-font-size": "1.5rem",
            maxWidth: "8rem",
            paddingTop: "4px",
            paddingBottom: "4px",
          } as CSSProperties
        }
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

export const FirstName = function () {
  const { register } = useFormContext();
  const { errors } = useFormState();
  const formValues = useWatch();

  const KEY = "firstName";
  const VALIDATION = { presence: true };
  const ERRORS = errors[KEY];

  return (
    <ElementWrapper hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} required={true} text="First Name" />
      <InputText
        registerProps={register(KEY, createValidation(VALIDATION, formValues))}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

export const LastName = function () {
  const { register } = useFormContext();
  const { errors } = useFormState();
  const formValues = useWatch();

  const KEY = "lastName";
  const VALIDATION = { presence: true };
  const ERRORS = errors[KEY];

  return (
    <ElementWrapper hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} required={true} text="Last Name" />
      <InputText
        registerProps={register(KEY, createValidation(VALIDATION, formValues))}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

export const Phone = function () {
  const { register } = useFormContext();
  const { errors } = useFormState();
  const formValues = useWatch();

  const KEY = "phone";
  const VALIDATION = { presence: true };
  const ERRORS = errors[KEY];
  const ERRORS_COUNTRY_CODE = errors[KEY + "-country-code"];

  const errorText = ERRORS
    ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
    : ERRORS_COUNTRY_CODE
      ? getErrorMessage(
          ERRORS_COUNTRY_CODE.type as TErrorMessageKey,
          VALIDATION,
        )
      : null;

  return (
    <ElementWrapper hasErrors={!!ERRORS || !!ERRORS_COUNTRY_CODE}>
      <Label htmlFor={KEY} required={true} text="Phone Number" />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
        }}
      >
        <select
          className="kup-select kup-select-arrow"
          {...register(
            KEY + "-country-code",
            createValidation(VALIDATION, formValues),
          )}
          style={{
            width: "8.25rem",
          }}
        >
          <CountryOptions />
        </select>
        <InputText
          registerProps={register(
            KEY,
            createValidation(VALIDATION, formValues),
          )}
        />
      </div>
      <ErrorMessage text={errorText} />
    </ElementWrapper>
  );
};

export const DOB = function () {
  const { register } = useFormContext();
  const { errors } = useFormState();
  const formValues = useWatch();

  const KEY = "dob";
  const VALIDATION = { presence: true };
  const ERRORS = errors[KEY];
  const now = new Date();
  const thirteenYearsAgo = new Date(
    now.getFullYear() - 13,
    now.getMonth(),
    now.getDate(),
  );

  return (
    <ElementWrapper hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} required={true} text="Date of Birth" />
      <DatePicker
        max={thirteenYearsAgo.toLocaleDateString()}
        registerProps={register(KEY, createValidation(VALIDATION, formValues))}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};

export const Age = function () {
  const { control, register } = useFormContext();
  const { errors } = useFormState();
  const formValues = useWatch();

  const KEY = "age";
  const VALIDATION = { presence: true };
  const ERRORS = errors[KEY];
  const OPTIONS = [
    {
      value: 13,
      key: "13",
    },
    {
      value: 14,
      key: "14",
    },
    {
      value: 15,
      key: "15",
    },
    {
      value: 16,
      key: "16",
    },
    {
      value: 17,
      key: "17",
    },
    {
      value: 18,
      key: "18+",
    },
  ];

  return (
    <ElementWrapper hasErrors={!!ERRORS}>
      <Label htmlFor={KEY} required text="Age" />
      <Controller
        defaultValue=""
        control={control}
        name="age"
        render={({ field }) => {
          return (
            <Select
              data={OPTIONS}
              displayKey="key"
              hasErrors={!!ERRORS}
              name="age"
              placeholderText="Please select"
              registerProps={register(KEY, {
                valueAsNumber: true,
                ...createValidation(VALIDATION, formValues),
              })}
              valueKey="value"
            />
          );
        }}
      />
      <ErrorMessage
        text={
          ERRORS
            ? getErrorMessage(ERRORS.type as TErrorMessageKey, VALIDATION)
            : null
        }
      />
    </ElementWrapper>
  );
};
